// src/pages/Home.js
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { getFeaturedProducts, getCategories } from "../utils/firestoreService";
import { getSliderSettings } from "../utils/settingsService";

// ── TREND SLIDER ──────────────────────────────────────
function TrendSlider({ slides }) {
  const [active,    setActive]    = useState(0);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef(null);

  const goTo = (idx) => {
    if (animating || idx === active) return;
    setAnimating(true);
    setTimeout(() => { setActive(idx); setAnimating(false); }, 280);
  };
  const next = () => goTo((active + 1) % slides.length);
  const prev = () => goTo((active - 1 + slides.length) % slides.length);

  useEffect(() => {
    intervalRef.current = setInterval(next, 5000);
    return () => clearInterval(intervalRef.current);
  }, [active, slides.length]);

  // Touch swipe en móvil
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (!touchStart.current) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    touchStart.current = null;
  };

  if (!slides.length) return null;
  const slide = slides[active];

  return (
    <div className="trend-slider">
      <div
        className={`trend-slide ${animating ? "ts-out-next" : "ts-in"}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <span className="trend-tag" style={{ background: slide.accent }}>{slide.tag}</span>

        <div className="trend-visual">
          {slide.img ? (
            <img src={slide.img} alt={slide.title} className="trend-img"/>
          ) : (
            <div className="trend-emoji-box" style={{ borderColor: slide.accent + "44" }}>
              <span className="trend-emoji">{slide.emoji}</span>
              <div className="trend-ring r1" style={{ borderColor: slide.accent + "33" }}/>
              <div className="trend-ring r2" style={{ borderColor: slide.accent + "1a" }}/>
            </div>
          )}
        </div>

        <div className="trend-info">
          <span className="trend-label" style={{ color: slide.accent }}>
            {slide.emoji} {slide.label}
          </span>
          <h3 className="trend-title">{slide.title}</h3>
          <p className="trend-desc">{slide.desc}</p>
          <Link to="/tienda" className="trend-cta" style={{ background: slide.accent }}>
            Ver colección →
          </Link>
        </div>

        <div className="trend-nav">
          <button className="trend-arrow" onClick={prev} aria-label="Anterior">‹</button>
          <div className="trend-dots">
            {slides.map((_, i) => (
              <button key={i}
                className={`trend-dot ${i === active ? "active" : ""}`}
                style={i === active ? { background: slide.accent, width: 20 } : {}}
                onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <button className="trend-arrow" onClick={next} aria-label="Siguiente">›</button>
        </div>
      </div>

      {/* Miniaturas — ocultas en móvil muy pequeño */}
      <div className="trend-thumbs">
        {slides.map((s, i) => (
          <button key={s.id || i}
            className={`trend-thumb-item ${i === active ? "active" : ""}`}
            style={i === active ? { borderColor: s.accent } : {}}
            onClick={() => goTo(i)}
          >
            {s.img
              ? <img src={s.img} alt={s.label} style={{ width:24, height:24, objectFit:"cover", borderRadius:5 }}/>
              : <span style={{ fontSize:18 }}>{s.emoji}</span>
            }
            <span className="trend-thumb-label">{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── HOME ──────────────────────────────────────────────
export default function Home() {
  const [featured,   setFeatured]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [slides,     setSlides]     = useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    Promise.all([getFeaturedProducts(), getCategories(), getSliderSettings()])
      .then(([prods, cats, slds]) => {
        setFeatured(prods);
        setCategories(cats);
        setSlides(slds);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <main>

      {/* ══ HERO ══════════════════════════════════════ */}
      <section className="hero-v2">
        <div className="hero-bg">
          <div className="hero-shape s1"/>
          <div className="hero-shape s2"/>
          <div className="hero-shape s3"/>
        </div>

        {/* IZQUIERDA — Slider */}
        <div className="hero-left">
          <p className="hero-left-tag">🔥 Productos de tendencia</p>
          {slides.length > 0 && <TrendSlider slides={slides}/>}
        </div>

        {/* DERECHA — Copy */}
        <div className="hero-right">
          <div className="hero-content">
            <span className="hero-tag">
              Hecho a mano · Impresión 3D · Armenia, Colombia
            </span>
            <h1>
              Piezas únicas<br/>
              <em>con corazón</em>
            </h1>
            <p>
              Decoración y souvenirs originales, fabricados capa por capa
              con tecnología 3D para hacer especial cada espacio y momento.
            </p>
            <div className="hero-ctas">
              <Link to="/tienda" className="btn-primary">Ver la tienda</Link>
              <Link to="/nosotros" className="btn-ghost">Nuestra historia →</Link>
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <strong>100%</strong>
                <span>Colombiano</span>
              </div>
              <div className="hero-stat">
                <strong>3D</strong>
                <span>Impresión propia</span>
              </div>
              <div className="hero-stat">
                <strong>🚚</strong>
                <span>Envío nacional</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PROCESO ═══════════════════════════════════ */}
      <section className="process-section">
        <h2 className="section-title" style={{ textAlign:"center" }}>
          Cómo lo hacemos
        </h2>
        <div className="process-steps">
          {[
            { icon:"✏️", step:"1", title:"Diseño digital",  desc:"Cada pieza comienza como un diseño 3D pensado con amor." },
            { icon:"🖨️", step:"2", title:"Impresión 3D",    desc:"Producción capa por capa con materiales ecológicos." },
            { icon:"🎨", step:"3", title:"Acabados",        desc:"Pintado, lijado y control de calidad manual." },
            { icon:"📦", step:"4", title:"Envío seguro",    desc:"Empaque especial y envío a todo Colombia." },
          ].map(s => (
            <div className="process-step" key={s.step}>
              <div className="step-icon">{s.icon}</div>
              <span className="step-num">Paso {s.step}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CATEGORÍAS ════════════════════════════════ */}
      {categories.length > 0 && (
        <section className="categories-section">
          <h2 className="section-title">Explora por categoría</h2>
          <div className="categories-grid">
            {categories.map(cat => (
              <Link key={cat.id} to={`/tienda?cat=${cat.slug}`} className="cat-card">
                {cat.image && <img src={cat.image} alt={cat.name}/>}
                <div className="cat-overlay">
                  <h3>{cat.name}</h3>
                  <span>Ver productos →</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ══ DESTACADOS ════════════════════════════════ */}
      <section className="featured-section">
        <div className="section-header">
          <h2 className="section-title">Destacados</h2>
          <Link to="/tienda" className="see-all">Ver todos →</Link>
        </div>
        {loading ? (
          <div className="skeleton-grid">
            {[1,2,3,4].map(i => <div key={i} className="skeleton-card"/>)}
          </div>
        ) : featured.length === 0 ? (
          <div className="empty-state">
            <p>Pronto habrá productos disponibles. ¡Vuelve pronto!</p>
          </div>
        ) : (
          <div className="products-grid">
            {featured.map(p => <ProductCard key={p.id} product={p}/>)}
          </div>
        )}
      </section>

      {/* ══ CTA ═══════════════════════════════════════ */}
      <section className="cta-section">
        <div className="cta-banner">
          <h2>¿Quieres algo personalizado?</h2>
          <p>Creamos piezas únicas con tu nombre, logo o diseño especial.</p>
          <a
            href="https://wa.me/573102631621?text=Hola!%20quiero%20un%20producto%20personalizado"
            target="_blank" rel="noreferrer"
            className="btn-whatsapp"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Escríbenos por WhatsApp
          </a>
        </div>
      </section>

      {/* ══ TESTIMONIOS ═══════════════════════════════ */}
      <section className="testimonials">
        <h2 className="section-title">Lo que dicen nuestros clientes</h2>
        <div className="testimonials-grid">
          {[
            { name:"María García",  stars:5, text:"La calidad es increíble. Los detalles son perfectos, se nota el amor con que las hacen." },
            { name:"Carlos Ruiz",   stars:5, text:"Compré un souvenir personalizado y quedó espectacular. El envío fue rápido y bien empacado." },
            { name:"Ana Martínez",  stars:5, text:"Hermosos diseños únicos y originales. Ya compré varias veces y siempre quedo encantada." },
          ].map((t, i) => (
            <div key={i} className="testimonial-card">
              <div className="test-stars">{"★".repeat(t.stars)}</div>
              <p>"{t.text}"</p>
              <span>— {t.name}</span>
            </div>
          ))}
        </div>
      </section>

    </main>
  );
}
